import React, { useCallback, useEffect, useState } from "react";
import { IoMdArrowRoundBack } from "react-icons/io";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import useAuthFetch from "../../hooks/useAuthFetch";
import Spinner from "../../components/Spinner";
import ProvinciaCantonDistritoSelector from "../../components/ProvinciaCantonDistritoSelector";
import { useSelector } from "react-redux";

// --- Asignación fija por distrito ---
const DistritoAsignacionSection = ({ authFetch, bodegas, nombresMap }) => {
  const [reglas, setReglas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    idProvincia: "",
    idCanton: "",
    idDistrito: "",
    idBodega: "",
    prioridad: "1",
  });

  const getNombreProvincia = (id) => nombresMap[`prov-${id}`] || id;
  const getNombreCanton = (provId, cantId) => nombresMap[`cant-${provId}-${cantId}`] || cantId;
  const getNombreDistrito = (provId, cantId, distId) => nombresMap[`dist-${provId}-${cantId}-${distId}`] || distId;

  const fetchReglas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch("asignacion-distrito", null, "GET");
      if (res && res.ok) {
        const data = await res.json();
        setReglas(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Error fetching asignacion-distrito:", err);
    }
    setLoading(false);
  }, [authFetch]);

  useEffect(() => {
    fetchReglas();
  }, [fetchReglas]);

  const handleAdd = async () => {
    const { idProvincia, idCanton, idDistrito, idBodega, prioridad } = form;
    if (!idProvincia || !idCanton || !idDistrito || !idBodega) {
      Swal.fire({
        icon: "warning",
        title: "Campos requeridos",
        text: "Debe completar provincia, cantón, distrito y bodega",
        background: "#76236C",
        confirmButtonColor: "#19D3C5",
        color: "#fafafa",
      });
      return;
    }

    const res = await authFetch(
      "asignacion-distrito",
      {
        idProvincia: parseInt(idProvincia),
        idCanton: parseInt(idCanton),
        idDistrito: parseInt(idDistrito),
        idBodega: parseInt(idBodega),
        prioridad: parseInt(prioridad) || 1,
        activo: 1,
      },
      "POST"
    );

    if (res && (res.status === 200 || res.status === 201 || res.status === 204)) {
      Swal.fire({
        icon: "success",
        title: "Correcto",
        text: "Regla de distrito creada",
        background: "#76236C",
        confirmButtonColor: "#19D3C5",
        color: "#fafafa",
      });
      setForm({ idProvincia: "", idCanton: "", idDistrito: "", idBodega: "", prioridad: "1" });
      fetchReglas();
    } else {
      const errorData = res ? await res.json().catch(() => null) : null;
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorData?.error?.message || "No se pudo crear la regla",
        background: "#76236C",
        confirmButtonColor: "#19D3C5",
        color: "#fafafa",
      });
    }
  };

  const handleToggleActivo = async (regla) => {
    const nuevoActivo = regla.activo === 1 ? 0 : 1;
    const res = await authFetch(
      `asignacion-distrito/${regla.idProvincia}/${regla.idCanton}/${regla.idDistrito}`,
      { activo: nuevoActivo },
      "PATCH"
    );

    if (res && (res.status === 200 || res.status === 204)) {
      fetchReglas();
    } else {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo actualizar la regla",
        background: "#76236C",
        confirmButtonColor: "#19D3C5",
        color: "#fafafa",
      });
    }
  };

  const handleDelete = async (regla) => {
    const result = await Swal.fire({
      title: "¿Eliminar esta regla?",
      text: `Distrito ${regla.idProvincia}-${regla.idCanton}-${regla.idDistrito}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#19D3C5",
      cancelButtonColor: "#E7004C",
      background: "#76236C",
      color: "#fafafa",
    });

    if (result.isConfirmed) {
      const res = await authFetch(
        `asignacion-distrito/${regla.idProvincia}/${regla.idCanton}/${regla.idDistrito}`,
        null,
        "DELETE"
      );

      if (res && (res.status === 200 || res.status === 204)) {
        Swal.fire({
          icon: "success",
          title: "Eliminada",
          text: "Regla eliminada correctamente",
          background: "#76236C",
          confirmButtonColor: "#19D3C5",
          color: "#fafafa",
        });
        fetchReglas();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo eliminar la regla",
          background: "#76236C",
          confirmButtonColor: "#19D3C5",
          color: "#fafafa",
        });
      }
    }
  };

  const getBodegaNombre = (idBodega) => {
    const b = bodegas.find((bod) => bod.id === idBodega);
    return b ? b.nombre : `Bodega #${idBodega}`;
  };

  return (
    <div className="mb-10 p-6 ring-1 ring-purple rounded-xl">
      <h3 className="text-lg font-bold text-baseGray mb-4">
        Asignación fija por distrito
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Asigna un distrito específico a una bodega de despacho de forma fija (prioridad 2 en la resolución).
      </p>

      {loading ? (
        <Spinner />
      ) : (
        <>
          {reglas.length > 0 ? (
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-purple/30">
                    <th className="text-left py-2">Provincia</th>
                    <th className="text-left py-2">Cantón</th>
                    <th className="text-left py-2">Distrito</th>
                    <th className="text-left py-2">Bodega</th>
                    <th className="text-left py-2">Prioridad</th>
                    <th className="text-center py-2">Activo</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {reglas.map((regla, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-2">{getNombreProvincia(regla.idProvincia)}</td>
                      <td className="py-2">{getNombreCanton(regla.idProvincia, regla.idCanton)}</td>
                      <td className="py-2">{getNombreDistrito(regla.idProvincia, regla.idCanton, regla.idDistrito)}</td>
                      <td className="py-2">{getBodegaNombre(regla.idBodega)}</td>
                      <td className="py-2">{regla.prioridad}</td>
                      <td className="py-2 text-center">
                        <button
                          type="button"
                          onClick={() => handleToggleActivo(regla)}
                          className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${
                            regla.activo === 1 ? "bg-cyan" : "bg-gray-300"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              regla.activo === 1 ? "translate-x-5" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </td>
                      <td className="py-2 text-right">
                        <button
                          type="button"
                          onClick={() => handleDelete(regla)}
                          className="text-pink hover:text-red-700 font-medium text-sm"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-sm mb-4">
              No hay reglas de asignación por distrito configuradas.
            </p>
          )}

          {/* Inline add form */}
          <div className="flex flex-wrap gap-2 items-end mt-4 pt-4 border-t border-purple/20">
            <div className="flex-1 min-w-[320px]">
              <ProvinciaCantonDistritoSelector
                onSelect={({ idProvincia, idCanton, idDistrito }) => {
                  setForm({ ...form, idProvincia: String(idProvincia), idCanton: String(idCanton), idDistrito: String(idDistrito) });
                }}
              />
            </div>
            <div>
              <label className="block text-xs mb-1">Bodega</label>
              <select
                className="w-40 rounded-xl h-10 px-3 ring-1 ring-purple outline-none text-sm bg-white"
                value={form.idBodega}
                onChange={(e) => setForm({ ...form, idBodega: e.target.value })}
              >
                <option value="">Seleccione...</option>
                {bodegas.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs mb-1">Prioridad</label>
              <input
                type="number"
                className="w-20 rounded-xl h-10 px-3 ring-1 ring-purple outline-none text-sm"
                value={form.prioridad}
                onChange={(e) => setForm({ ...form, prioridad: e.target.value })}
                placeholder="1"
                min="1"
              />
            </div>
            <button
              type="button"
              onClick={handleAdd}
              className="bg-cyan text-white py-2 px-5 font-bold rounded-full text-sm"
            >
              Agregar
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// --- Reglas por dirección ---
const DireccionReglasSection = ({ authFetch, bodegas }) => {
  const [reglas, setReglas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    patron: "",
    idBodega: "",
    prioridad: "1",
  });

  const fetchReglas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch("asignacion-direccion", null, "GET");
      if (res && res.ok) {
        const data = await res.json();
        setReglas(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Error fetching asignacion-direccion:", err);
    }
    setLoading(false);
  }, [authFetch]);

  useEffect(() => {
    fetchReglas();
  }, [fetchReglas]);

  const handleAdd = async () => {
    const { patron, idBodega, prioridad } = form;
    if (!patron || !idBodega) {
      Swal.fire({
        icon: "warning",
        title: "Campos requeridos",
        text: "Debe completar el patrón y seleccionar una bodega",
        background: "#76236C",
        confirmButtonColor: "#19D3C5",
        color: "#fafafa",
      });
      return;
    }

    const res = await authFetch(
      "asignacion-direccion",
      {
        patron,
        idBodega: parseInt(idBodega),
        prioridad: parseInt(prioridad) || 1,
        activo: 1,
      },
      "POST"
    );

    if (res && (res.status === 200 || res.status === 201 || res.status === 204)) {
      Swal.fire({
        icon: "success",
        title: "Correcto",
        text: "Regla por dirección creada",
        background: "#76236C",
        confirmButtonColor: "#19D3C5",
        color: "#fafafa",
      });
      setForm({ patron: "", idBodega: "", prioridad: "1" });
      fetchReglas();
    } else {
      const errorData = res ? await res.json().catch(() => null) : null;
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorData?.error?.message || "No se pudo crear la regla",
        background: "#76236C",
        confirmButtonColor: "#19D3C5",
        color: "#fafafa",
      });
    }
  };

  const handleToggleActivo = async (regla) => {
    const nuevoActivo = regla.activo === 1 ? 0 : 1;
    const res = await authFetch(
      `asignacion-direccion/${regla.id}`,
      { activo: nuevoActivo },
      "PATCH"
    );

    if (res && (res.status === 200 || res.status === 204)) {
      fetchReglas();
    } else {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo actualizar la regla",
        background: "#76236C",
        confirmButtonColor: "#19D3C5",
        color: "#fafafa",
      });
    }
  };

  const handleDelete = async (regla) => {
    const result = await Swal.fire({
      title: "¿Eliminar esta regla?",
      text: `Patrón: "${regla.patron}"`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#19D3C5",
      cancelButtonColor: "#E7004C",
      background: "#76236C",
      color: "#fafafa",
    });

    if (result.isConfirmed) {
      const res = await authFetch(
        `asignacion-direccion/${regla.id}`,
        null,
        "DELETE"
      );

      if (res && (res.status === 200 || res.status === 204)) {
        Swal.fire({
          icon: "success",
          title: "Eliminada",
          text: "Regla eliminada correctamente",
          background: "#76236C",
          confirmButtonColor: "#19D3C5",
          color: "#fafafa",
        });
        fetchReglas();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo eliminar la regla",
          background: "#76236C",
          confirmButtonColor: "#19D3C5",
          color: "#fafafa",
        });
      }
    }
  };

  const getBodegaNombre = (idBodega) => {
    const b = bodegas.find((bod) => bod.id === idBodega);
    return b ? b.nombre : `Bodega #${idBodega}`;
  };

  return (
    <div className="mb-10 p-6 ring-1 ring-purple rounded-xl">
      <h3 className="text-lg font-bold text-baseGray mb-4">
        Reglas por dirección
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Patrones de texto que, al hacer match con la dirección del cliente, asignan una bodega de despacho (prioridad 1, máxima).
      </p>

      {loading ? (
        <Spinner />
      ) : (
        <>
          {reglas.length > 0 ? (
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-purple/30">
                    <th className="text-left py-2">ID</th>
                    <th className="text-left py-2">Patrón</th>
                    <th className="text-left py-2">Bodega</th>
                    <th className="text-left py-2">Prioridad</th>
                    <th className="text-center py-2">Activo</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {reglas.map((regla) => (
                    <tr key={regla.id} className="border-b border-gray-100">
                      <td className="py-2">{regla.id}</td>
                      <td className="py-2 font-mono text-xs">{regla.patron}</td>
                      <td className="py-2">{getBodegaNombre(regla.idBodega)}</td>
                      <td className="py-2">{regla.prioridad}</td>
                      <td className="py-2 text-center">
                        <button
                          type="button"
                          onClick={() => handleToggleActivo(regla)}
                          className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${
                            regla.activo === 1 ? "bg-cyan" : "bg-gray-300"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              regla.activo === 1 ? "translate-x-5" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </td>
                      <td className="py-2 text-right">
                        <button
                          type="button"
                          onClick={() => handleDelete(regla)}
                          className="text-pink hover:text-red-700 font-medium text-sm"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-sm mb-4">
              No hay reglas por dirección configuradas.
            </p>
          )}

          {/* Inline add form */}
          <div className="flex flex-wrap gap-2 items-end mt-4 pt-4 border-t border-purple/20">
            <div>
              <label className="block text-xs mb-1">Patrón (regex/substring)</label>
              <input
                type="text"
                className="w-48 rounded-xl h-10 px-3 ring-1 ring-purple outline-none text-sm"
                value={form.patron}
                onChange={(e) => setForm({ ...form, patron: e.target.value })}
                placeholder="guachipel[ií]n"
              />
            </div>
            <div>
              <label className="block text-xs mb-1">Bodega</label>
              <select
                className="w-40 rounded-xl h-10 px-3 ring-1 ring-purple outline-none text-sm bg-white"
                value={form.idBodega}
                onChange={(e) => setForm({ ...form, idBodega: e.target.value })}
              >
                <option value="">Seleccione...</option>
                {bodegas.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs mb-1">Prioridad</label>
              <input
                type="number"
                className="w-20 rounded-xl h-10 px-3 ring-1 ring-purple outline-none text-sm"
                value={form.prioridad}
                onChange={(e) => setForm({ ...form, prioridad: e.target.value })}
                placeholder="1"
                min="1"
              />
            </div>
            <button
              type="button"
              onClick={handleAdd}
              className="bg-cyan text-white py-2 px-5 font-bold rounded-full text-sm"
            >
              Agregar
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// --- Main Page Component ---
const AsignacionRules = () => {
  const { token } = useSelector((store) => store.app.user);
  const authFetch = useAuthFetch();
  const [bodegas, setBodegas] = useState([]);
  const [loadingBodegas, setLoadingBodegas] = useState(true);
  const [nombresMap, setNombresMap] = useState({});

  // Fetch all active bodegas and geographic names
  useEffect(() => {
    const fetchData = async () => {
      setLoadingBodegas(true);
      try {
        const res = await authFetch(
          'bodegas?filter={"where":{"activo":1},"order":["nombre asc"]}',
          null,
          "GET"
        );
        if (res && res.ok) {
          const data = await res.json();
          setBodegas(Array.isArray(data) ? data : []);
        }

        // Cargar nombres geográficos
        const map = {};
        const provRes = await authFetch("provincias", null, "GET");
        if (provRes && provRes.ok) {
          const provs = await provRes.json();
          for (const p of provs) {
            map[`prov-${p.idProvincia}`] = p.nombre;
            // Cargar cantones de cada provincia
            const cantFilter = JSON.stringify({ where: { idProvincia: p.idProvincia } });
            const cantRes = await authFetch(`cantones?filter=${encodeURIComponent(cantFilter)}`, null, "GET");
            if (cantRes && cantRes.ok) {
              const cants = await cantRes.json();
              for (const c of cants) {
                map[`cant-${c.idProvincia}-${c.idCanton}`] = c.nombre;
              }
            }
          }
        }
        // Cargar todos los distritos para resolver nombres
        const distRes = await authFetch("distritos", null, "GET");
        if (distRes && distRes.ok) {
          const dists = await distRes.json();
          for (const d of dists) {
            map[`dist-${d.idProvincia}-${d.idCanton}-${d.idDistrito}`] = d.nombre;
          }
        }
        setNombresMap(map);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
      setLoadingBodegas(false);
    };
    fetchData();
  }, [authFetch]);

  return (
    <div className="w-full p-content pb-16">
      <div className="flex w-full items-center">
        <div className="w-full">
          <Link to="/bodegas">
            <button className="bg-pink text-white py-2 px-8 font-bold rounded-full float-right flex justify-center items-center">
              <IoMdArrowRoundBack className="text-lg" /> Volver
            </button>
          </Link>
        </div>
      </div>

      <h2 className="my-3 font-bold text-baseGray text-2xl">
        Reglas de Asignación de Bodega
      </h2>
      <p className="text-sm text-gray-500 mb-6">
        Configure cómo se asigna automáticamente la bodega de despacho para las ventas.
        La resolución sigue esta precedencia: 1) Regla por dirección, 2) Asignación fija por distrito, 3) Por distancia.
      </p>

      {loadingBodegas ? (
        <Spinner />
      ) : (
        <>
          <DireccionReglasSection authFetch={authFetch} bodegas={bodegas} />
          <DistritoAsignacionSection authFetch={authFetch} bodegas={bodegas} nombresMap={nombresMap} />
        </>
      )}
    </div>
  );
};

export default AsignacionRules;
